import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { findByTurma, findUnregisteredDisciplinas, registerNBAndCA, remove, save } from "./api-tdp";
import { findByPerfil } from "../Usuario/api-usuario-perfil";
import { findAll as findAllLivros } from "../Livro/api-livro";
import { ADMIN, COORDENADOR, PROFESSOR } from "../Usuario/Perfil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import TurmaProfile from "../Turma/TurmaProfile";
import TurmaTableSelect from "../Turma/TurmaTableSelect";
import DisciplinaTurmaForm from "./DisciplinaTurmaForm";
import DisciplinaTurmaTable from "./DisciplinaTurmaTable";

const porDescricao = (a, b) => (a.descricao ?? "").localeCompare(b.descricao ?? "", "pt-BR");
const porDisciplina = (a, b) => porDescricao(a.disciplina ?? {}, b.disciplina ?? {});

const mensagemDe = (data, padrao) =>
  [].concat(data?.message ?? []).filter(Boolean).join("\n") || padrao;

/** Mensagens do servidor que têm texto próprio para o usuário. */
const MENSAGENS_SAVE = {
  "Erro: Bimestre nao cadastrado":
    "A série desta turma não possui bimestres cadastrados. Cadastre os bimestres antes de inserir disciplinas na turma.",
  "Erro: Turma and Disciplina already registered": "Disciplina já cadastrada para a turma.",
};

export default function DisciplinaTurmaContainer() {
  const usuario = useSelector((state) => state.usuario);
  const podeRemover = usuario?.perfil === ADMIN || usuario?.perfil === COORDENADOR;

  const [turma, setTurma] = useState(null);
  const [tdps, setTdps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unregisteredDisciplinas, setUnregisteredDisciplinas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [livros, setLivros] = useState([]);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [registrando, setRegistrando] = useState(null);
  const codTurma = turma?.codigo;

  useEffect(() => {
    findByPerfil(PROFESSOR).then((data) => {
      if (Array.isArray(data)) {
        setProfessores(
          data.filter((p) => p?.usuario).sort((a, b) => (a.usuario.nome ?? "").localeCompare(b.usuario.nome ?? "")),
        );
      } else if (data?.error) {
        errorMessage(mensagemDe(data, "Não foi possível carregar os professores."));
      }
    });
    findAllLivros().then((data) => {
      if (Array.isArray(data)) {
        setLivros([...data].sort(porDescricao));
      } else if (data?.error) {
        errorMessage(mensagemDe(data, "Não foi possível carregar os livros."));
      }
    });
  }, []);

  useEffect(() => {
    setTdps([]);
    setUnregisteredDisciplinas([]);
    setEditando(null);
    if (!codTurma) return;
    let active = true;

    findUnregisteredDisciplinas(codTurma).then((data) => {
      if (!active || !data) return;
      if (data.error) {
        errorMessage(mensagemDe(data, "Não foi possível carregar as disciplinas disponíveis."));
      } else if (Array.isArray(data)) {
        setUnregisteredDisciplinas([...data].sort(porDescricao));
      }
    });

    setIsLoading(true);
    findByTurma(codTurma)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data, "Não foi possível carregar as disciplinas da turma."));
        } else if (Array.isArray(data)) {
          setTdps([...data].sort(porDisciplina));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [codTurma]);

  const handleSave = (persist) => {
    const eNovo = !persist.codigo;
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(MENSAGENS_SAVE[data?.message] ?? mensagemDe(data, "Não foi possível salvar a disciplina da turma."));
          return;
        }
        if (eNovo) {
          setTdps((prevState) => [...prevState, data].sort(porDisciplina));
          setUnregisteredDisciplinas((prevState) =>
            prevState.filter((d) => d.codDisciplina !== data.disciplina?.codDisciplina),
          );
        } else {
          setTdps((prevState) => prevState.map((t) => (t.codigo === data.codigo ? data : t)));
        }
        setEditando(null);
        success("Salvo com sucesso.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleRegisterNBAndCA = (tdp) => {
    setRegistrando(tdp.codigo);
    registerNBAndCA(tdp.codigo)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível registrar as notas bimestrais e conceitos."));
          return;
        }
        success(`Notas bimestrais e conceitos registrados para ${tdp.disciplina?.descricao}.`);
      })
      .finally(() => setRegistrando(null));
  };

  const handleDelete = (tdp) =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a disciplina "${tdp.disciplina?.descricao}" da turma? ATENÇÃO: as notas bimestrais desta disciplina serão excluídas após a confirmação.`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(tdp.codigo).then((data) => {
              if (!data || data.error) {
                errorMessage(
                  mensagemDe(
                    data,
                    "A disciplina não pode ser removida da turma pois possui presenças/atividades cadastradas. Remova esses registros antes.",
                  ),
                );
                return;
              }
              setTdps((prevState) => prevState.filter((t) => t.codigo !== tdp.codigo));
              if (tdp.disciplina) {
                setUnregisteredDisciplinas((prevState) => [...prevState, tdp.disciplina].sort(porDescricao));
              }
              success("Disciplina removida da turma.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  return (
    <Container>
      <DisciplinaTurmaForm
        tdp={editando}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        isSaving={isSaving}
        unregisteredDisciplinas={unregisteredDisciplinas}
        professores={professores}
        livros={livros}
      />

      <Box mb={3}>
        <Typography variant="h6">Disciplinas da Turma</Typography>
        <Typography variant="body2" color="text.secondary">
          Disciplinas oferecidas em cada turma, com professor, livro e carga horária.
        </Typography>
      </Box>

      {!turma ? (
        <TurmaTableSelect onTurmaSelected={setTurma} />
      ) : (
        <Stack spacing={3}>
          <TurmaProfile turma={turma} handleTrocaTurma={() => setTurma(null)} />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                Disciplinas cadastradas
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isLoading || unregisteredDisciplinas.length === 0}
                onClick={() => setEditando({ eNovo: true, turma, eDataSerie: true })}
              >
                Adicionar Disciplina
              </Button>
            </Stack>
            <DisciplinaTurmaTable
              tdps={tdps}
              isLoading={isLoading}
              registrando={registrando}
              podeRemover={podeRemover}
              onEdit={setEditando}
              onRegister={handleRegisterNBAndCA}
              onDelete={handleDelete}
            />
          </Box>
        </Stack>
      )}
    </Container>
  );
}
